// public/v3/src/lib/pain-suggest.js — Chronic Pain Management candidacy
// heuristic for the Onboard pain lens. Mirrors bhi-suggest.js: pure CJS, a
// patient is CPM-suggestible when the chart shows a chronic-pain condition
// (by ICD-10 or description text) OR a high-signal pain medication.
//
// A suggestion is decision support only — G3002 additionally requires an
// in-person >=30-minute initiating visit by the billing practitioner, which
// no chart heuristic can attest. The enroll action carries that attestation.

// ICD-10 prefixes with a chronic-pain identity. G89 is the chronic-pain
// family proper; the M-codes are the chronic musculoskeletal drivers that
// dominate real CPM panels.
const PAIN_ICD10_RE = /\b(G89(\.\d+)?|M54(\.\d+)?|M79\.7|M79\.6\d*|M25\.5\d*|M1[5-9](\.\d+)?|M47(\.\d+)?|M51(\.\d+)?|G62\.9|E11\.4\d*)/i;

const PAIN_TEXT_RE = /\b(chronic\s+pain|fibromyalgia|low\s+back\s+pain|lumbago|back\s+pain|neuropath\w*|radiculopath\w*|neuralgia|osteoarthr\w*|degenerative\s+(disc|joint)|spinal\s+stenosis|sciatica|arthralgia|cervicalgia|complex\s+regional\s+pain|CRPS)/i;

// High-signal pain pharmacotherapy. Duloxetine is deliberately absent — it
// reads as a depression signal at least as often as a pain one and already
// feeds bhi-suggest; double-counting it would over-suggest CPM.
const PAIN_MED_GENERICS = [
  'oxycodone', 'hydrocodone', 'morphine', 'hydromorphone', 'oxymorphone',
  'fentanyl', 'methadone', 'tramadol', 'tapentadol', 'codeine',
  'gabapentin', 'pregabalin',
  'lidocaine patch', 'lidocaine 5%', 'diclofenac gel', 'diclofenac topical',
  'celecoxib', 'meloxicam', 'nabumetone', 'etodolac',
  'cyclobenzaprine', 'tizanidine', 'baclofen', 'methocarbamol',
];

function _conditionSignal(conditions) {
  for (const c of Array.isArray(conditions) ? conditions : []) {
    if (!c) continue;
    const icds = Array.isArray(c.icd10) ? c.icd10 : (c.icd10 ? [c.icd10] : []);
    for (const code of icds) {
      if (PAIN_ICD10_RE.test(String(code || ''))) {
        return { kind: 'dx', label: c.description || String(code) };
      }
    }
    if (PAIN_TEXT_RE.test(String(c.description || ''))) {
      return { kind: 'dx', label: c.description };
    }
  }
  return null;
}

function _medSignal(medications) {
  for (const m of Array.isArray(medications) ? medications : []) {
    const name = String((m && (m.name || m)) || '').toLowerCase();
    if (!name) continue;
    for (const g of PAIN_MED_GENERICS) {
      if (name.includes(g)) return { kind: 'med', label: (m && m.name) || g };
    }
  }
  return null;
}

// raw = patient data blob. Returns { eligible, reasons: string[] }.
function suggestPain(raw) {
  const p = raw && raw._raw ? raw._raw : (raw || {});
  const reasons = [];
  const dx = _conditionSignal(p.conditions);
  if (dx) reasons.push('Pain condition on chart: ' + dx.label);
  const med = _medSignal(p.medications);
  if (med) reasons.push('Pain medication on file: ' + med.label);
  return { eligible: reasons.length > 0, reasons };
}

module.exports = { suggestPain, PAIN_ICD10_RE, PAIN_TEXT_RE, PAIN_MED_GENERICS };
